import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useOrders, CustomerData } from "@/hooks/useOrders";
import { CartItem } from "@/types/CarItem";

interface CustomerFormProps {
  items: CartItem[];
  checkoutUrl: string;
  onCancel: () => void;
}

const CustomerForm = ({ items, checkoutUrl, onCancel }: CustomerFormProps) => {
  const { createOrder } = useOrders();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CustomerData, string>>>({});

  const [formData, setFormData] = useState<CustomerData>({
    name: "",
    email: "",
    phone: "",
    tax_id: "",
    address: "",
    city: "",
    state: "",
    zipcode: "",
  });

  // Máscara de CPF: 000.000.000-00
  const maskCPF = (value: string) => {
    return value
      .replace(/\D/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1");
  };

  // Máscara de Telefone: (00) 00000-0000
  const maskPhone = (value: string) => {
    return value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .replace(/(-\d{4})\d+?$/, "$1");
  };

  // Máscara de CEP: 00000-000
  const maskZipCode = (value: string) => {
    return value
      .replace(/\D/g, "")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .replace(/(-\d{3})\d+?$/, "$1");
  };

  // Validação de email
  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Validação de CPF
  const validateCPF = (cpf: string) => {
    const cleaned = cpf.replace(/\D/g, "");
    return cleaned.length === 11;
  };

  const handleInputChange = (field: keyof CustomerData, value: string) => {
    let maskedValue = value;

    if (field === "tax_id") {
      maskedValue = maskCPF(value);
    } else if (field === "phone") {
      maskedValue = maskPhone(value);
    } else if (field === "zipcode") {
      maskedValue = maskZipCode(value);
    }

    setFormData((prev) => ({ ...prev, [field]: maskedValue }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CustomerData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Email inválido";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Telefone é obrigatório";
    } else if (formData.phone.replace(/\D/g, "").length < 11) {
      newErrors.phone = "Telefone inválido";
    }

    if (!formData.tax_id.trim()) {
      newErrors.tax_id = "CPF é obrigatório";
    } else if (!validateCPF(formData.tax_id)) {
      newErrors.tax_id = "CPF inválido";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Endereço é obrigatório";
    }

    if (!formData.city.trim()) {
      newErrors.city = "Cidade é obrigatória";
    }

    if (!formData.state.trim()) {
      newErrors.state = "Estado é obrigatório";
    }

    if (!formData.zipcode.trim()) {
      newErrors.zipcode = "CEP é obrigatório";
    } else if (formData.zipcode.replace(/\D/g, "").length !== 8) {
      newErrors.zipcode = "CEP inválido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await createOrder(items, formData);

      if (result.success && checkoutUrl) {
        // Redirecionar para o link do PagSeguro do produto
        window.location.href = checkoutUrl;
      } else {
        throw new Error("Link de checkout não disponível");
      }
    } catch (error) {
      console.error("Erro ao criar pedido:", error);
      alert("Erro ao processar pedido. Por favor, tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-background rounded-lg p-6 md:p-8 shadow-lg w-full"
    >
      <h2 className="text-2xl font-semibold text-foreground mb-3">
        Dados para Entrega
      </h2>

      {/* Info Box */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
        <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Como funciona:</strong> Ao finalizar o pedido, você será direcionado para o checkout de pagamento do PagSeguro. 
          Após realizar o pagamento, você receberá a confirmação do pedido no WhatsApp, juntamente com os dados de entrega, 
          código de rastreio e tudo mais que precisar. Nosso atendimento é humanizado e estamos sempre prontos para ajudar! ✨
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Nome e Email em grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Nome Completo *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                errors.name ? "border-red-500" : "border-border"
              } bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm`}
              placeholder="Seu nome completo"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                errors.email ? "border-red-500" : "border-border"
              } bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm`}
              placeholder="seu@email.com"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>
        </div>

        {/* Telefone e CPF */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Telefone *
            </label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                errors.phone ? "border-red-500" : "border-border"
              } bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm`}
              placeholder="(00) 00000-0000"
              maxLength={15}
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              CPF *
            </label>
            <input
              type="text"
              value={formData.tax_id}
              onChange={(e) => handleInputChange("tax_id", e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                errors.tax_id ? "border-red-500" : "border-border"
              } bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm`}
              placeholder="000.000.000-00"
              maxLength={14}
            />
            {errors.tax_id && <p className="text-red-500 text-xs mt-1">{errors.tax_id}</p>}
          </div>
        </div>

        {/* Endereço */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Endereço *
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleInputChange("address", e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border ${
              errors.address ? "border-red-500" : "border-border"
            } bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm`}
            placeholder="Rua, número, complemento"
          />
          {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
        </div>

        {/* Cidade, Estado e CEP */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Cidade *
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                errors.city ? "border-red-500" : "border-border"
              } bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm`}
              placeholder="Sua cidade"
            />
            {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Estado *
            </label>
            <input
              type="text"
              value={formData.state}
              onChange={(e) => handleInputChange("state", e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                errors.state ? "border-red-500" : "border-border"
              } bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm`}
              placeholder="UF"
              maxLength={2}
            />
            {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
          </div>

          <div className="col-span-2 md:col-span-1">
            <label className="block text-sm font-medium text-foreground mb-1">
              CEP *
            </label>
            <input
              type="text"
              value={formData.zipcode}
              onChange={(e) => handleInputChange("zipcode", e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                errors.zipcode ? "border-red-500" : "border-border"
              } bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm`}
              placeholder="00000-000"
              maxLength={9}
            />
            {errors.zipcode && <p className="text-red-500 text-xs mt-1">{errors.zipcode}</p>}
          </div>
        </div>

        {/* Botões */}
        <div className="flex gap-3 pt-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-6 py-2.5 rounded-full border-2 border-border text-foreground font-medium hover:bg-muted transition-all disabled:opacity-50 text-sm"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processando...
              </>
            ) : (
              "Finalizar Pedido"
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default CustomerForm;
